import React from 'react'
import { createContext } from 'lib/createContext'
import { useDayforceJobQuery } from 'queries/admin/dayforce/useDayforceJobQuery'
import { Loading, Text } from '@workwhile/ui'

type DayforceJobContextValue = {
  state: {
    agencyId: string
    jobId: string
    data: NonNullable<ReturnType<typeof useDayforceJobQuery>['data']>
    isPending: boolean
    isError: boolean
    error: ReturnType<typeof useDayforceJobQuery>['error'] | null
  }
  actions: {
    refetch: () => void
  }
}

const [Context, useDayforceJobContextValue] =
  createContext<DayforceJobContextValue>({
    name: 'DayforceJobContext',
  })

type DayforceJobProviderProps = {
  children: React.ReactNode
  agencyId: string
  jobId: string
}

export const DayforceJobProvider = ({
  children,
  agencyId,
  jobId,
}: DayforceJobProviderProps) => {
  const { data, isPending, isError, error, refetch } = useDayforceJobQuery(
    agencyId,
    jobId
  )

  if (isPending) {
    return <Loading />
  }

  if (!data || isError) {
    return <Text>Error loading dayforce details</Text>
  }

  const value: DayforceJobContextValue = {
    state: {
      agencyId,
      jobId,
      data,
      isPending,
      isError,
      error: error ?? null,
    },
    actions: {
      refetch: () => {
        void refetch()
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useDayforceJobContextValue }
